import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { createElement } from 'react';
import {
  CircleUser,
  UserRound,
  Wallet,
  KeyRound,
  Megaphone,
  Building2,
  type LucideIcon,
} from 'lucide-react';
import { Container, PageHeader, type ContextualNavSection } from '@vnetwork/ui/layout';
import { useSetContextualNav } from '@/components/layout/nav-context';
import { brand } from '@/config/brand';
import { AccountInformation } from './information/AccountInformation';
import { ApiKeysPage } from './apikeys/ApiKeysPage';
import { AffiliatePage } from './affiliate/AffiliatePage';
import { BillingPage } from './billing/BillingPage';
import { ORG_BASE } from '@/pages/services/organizations/organizations-service';

const BASE = '/account';

interface AccountNavEntry {
  label: string;
  slug: string;
  icon: LucideIcon;
  /** Absolute destination for cross-area entries (e.g. Organization lives under /services). */
  href?: string;
}

/** Whitelabel toggle per account section — undefined = always on. */
const NAV_FEATURE: Record<string, boolean | undefined> = {
  billing: brand.features.billing,
  'api-keys': brand.features.apiKeys,
  affiliate: brand.features.affiliate,
};

const ACCOUNT_NAV: AccountNavEntry[] = [
  { label: 'Information', slug: 'information', icon: UserRound },
  { label: 'Billing', slug: 'billing', icon: Wallet },
  { label: 'API Keys', slug: 'api-keys', icon: KeyRound },
  { label: 'Affiliate', slug: 'affiliate', icon: Megaphone },
  // Organization now lives under /services/organizations — link out to its overview.
  { label: 'Organization', slug: 'organization', icon: Building2, href: `${ORG_BASE}/overview` },
].filter((e) => NAV_FEATURE[e.slug] !== false);

const hrefFor = (entry: AccountNavEntry) => entry.href ?? `${BASE}/${entry.slug}`;

/** Active nav id for the current pathname (longest match wins). */
function accountActiveId(pathname: string): string {
  const matches = ACCOUNT_NAV.map((e) => hrefFor(e))
    .filter((href) => pathname === href || pathname.startsWith(`${href}/`))
    .sort((a, b) => b.length - a.length);
  return matches[0] ?? `${BASE}/information`;
}

/**
 * Account area — Phase 2.
 *
 *   /account                 → redirect to /account/information
 *   /account/information      → profile, security (password + 2FA), app settings
 *   /account/billing          → credits, invoices, transactions, invoice detail
 *   /account/api-keys         → API key management
 *   /account/affiliate        → affiliate dashboard, analytics, payouts, settings
 *   /account/organization     → company profile + projects/teams links
 *
 * Sub-navigation lives in the app's scope-driven `ContextualSidebar` (registered
 * via `useSetContextualNav`) instead of an in-page rail — matching the per-domain
 * scope pattern used across Services.
 */
export default function AccountPage() {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  useSetContextualNav(() => {
    const sections: ContextualNavSection[] = [
      {
        items: ACCOUNT_NAV.map((entry) => ({
          id: hrefFor(entry),
          label: entry.label,
          icon: createElement(entry.icon, { size: 16 }),
          onSelect: () => navigate(hrefFor(entry)),
        })),
      },
    ];
    return {
      sections,
      activeId: accountActiveId(pathname),
      back: { label: 'Back to Overview', onSelect: () => navigate('/') },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, navigate]);

  return (
    <Container size="xl">
      <PageHeader
        title="Account"
        description="Your profile, security, billing and organization settings."
        icon={<CircleUser size={20} />}
      />

      <Routes>
        <Route index element={<Navigate to="information" replace />} />
        <Route path="information" element={<AccountInformation />} />
        {brand.features.billing && <Route path="billing/*" element={<BillingPage />} />}
        {brand.features.apiKeys && <Route path="api-keys" element={<ApiKeysPage />} />}
        {brand.features.affiliate && <Route path="affiliate/*" element={<AffiliatePage />} />}
        {/* Organization section moved to /services/organizations */}
        <Route path="organization/*" element={<Navigate to={`${ORG_BASE}/overview`} replace />} />
        <Route path="*" element={<Navigate to="information" replace />} />
      </Routes>
    </Container>
  );
}
